/**
 * System Health details hygiene.
 *
 * Service-status payloads (especially vault / vault-token) can include
 * token accessors and secret-shaped strings. The glass must never render
 * those values. IDs that must appear are name-only or last4.
 */

const FULL_REDACT_KEY_RE =
  /(^|[_-])(token|secret|password|passwd|authorization|bearer|credential|private[_-]?key|api[_-]?key|client[_-]?secret)([_-]|$)/i;

const ID_REDACT_KEY_RE = /(^|[_-])(accessor|client[_-]?id|key[_-]?id)([_-]|$)/i;

/** Vault service tokens (hvs.), legacy tokens (s.), and similar secret prefixes. */
const SECRET_SHAPED_RE =
  /^(hvs\.|s\.)[A-Za-z0-9+/=._-]{8,}$|^(sk-|ak_|key_)[A-Za-z0-9_-]{8,}$/i;

/** HashiCorp Vault accessors are typically 20–28 URL-safe alphanumerics. */
const VAULT_ACCESSOR_VALUE_RE = /^[A-Za-z0-9]{20,28}$/;

const REDACTED = '[redacted]';

export function last4(value: string): string {
  const text = String(value || '');
  if (text.length < 4) return REDACTED;
  return `…${text.slice(-4)}`;
}

export function isSensitiveHealthKey(key: string): boolean {
  const name = String(key || '').trim();
  if (!name) return false;
  return FULL_REDACT_KEY_RE.test(name) || ID_REDACT_KEY_RE.test(name);
}

export function looksLikeSecretString(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  const text = value.trim();
  if (!text) return false;
  return SECRET_SHAPED_RE.test(text) || VAULT_ACCESSOR_VALUE_RE.test(text);
}

function redactScalar(key: string, value: unknown): unknown {
  if (value == null) return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return isSensitiveHealthKey(key) ? REDACTED : value;
  }
  if (typeof value !== 'string') return value;

  const name = String(key || '');
  if (FULL_REDACT_KEY_RE.test(name) || SECRET_SHAPED_RE.test(value.trim())) {
    return REDACTED;
  }
  if (ID_REDACT_KEY_RE.test(name) || VAULT_ACCESSOR_VALUE_RE.test(value.trim())) {
    return last4(value);
  }
  return value;
}

export function sanitizeHealthDetails(input: unknown, key = ''): unknown {
  if (input == null) return input;
  if (Array.isArray(input)) {
    return input.map((item) => sanitizeHealthDetails(item, key));
  }
  if (typeof input === 'object') {
    const out: Record<string, unknown> = {};
    for (const [childKey, value] of Object.entries(input as Record<string, unknown>)) {
      out[childKey] = sanitizeHealthDetails(value, childKey);
    }
    return out;
  }
  return redactScalar(key, input);
}

export function formatHealthDetailLabel(key: string): string {
  return String(key || '').replace(/_/g, ' ');
}

export function formatHealthDetailValue(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

export type HealthDetailLine = { key: string; label: string; value: string };

/** Display-safe Details rows. Sensitive keys are already redacted. */
export function formatHealthDetailsLines(
  details: Record<string, unknown> | null | undefined,
  limit?: number,
): HealthDetailLine[] {
  if (!details || typeof details !== 'object') return [];
  const sanitized = sanitizeHealthDetails(details) as Record<string, unknown>;
  const lines = Object.entries(sanitized).map(([key, value]) => ({
    key,
    label: formatHealthDetailLabel(key),
    value: formatHealthDetailValue(value),
  }));
  if (typeof limit !== 'number') return lines;
  const useful = lines.filter((line) => !isSensitiveHealthKey(line.key));
  return (useful.length ? useful : lines).slice(0, limit);
}

/** Text dump used by the tiles and by the hygiene test. */
export function renderHealthDetailsDump(
  details: Record<string, unknown> | null | undefined,
): string {
  const lines = formatHealthDetailsLines(details);
  if (!lines.length) return '';
  return ['Details:', ...lines.map((line) => `${line.label}: ${line.value}`)].join('\n');
}

export function renderHealthDetailsJson(
  details: Record<string, unknown> | null | undefined,
): string {
  try {
    return JSON.stringify(sanitizeHealthDetails(details ?? {}), null, 2);
  } catch {
    return REDACTED;
  }
}

/** 0ms is not a useful measurement — keep status, hide the fake latency. */
export function formatHealthLatencyMs(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms <= 0) return '—';
  return `${Math.round(ms)}ms`;
}

/** Cache MISS is operational noise on this page — only paint a HIT. */
export function formatHealthCacheLine(
  cache?: { fresh?: boolean; age_ms?: number } | null,
): string | null {
  if (!cache?.fresh) return null;
  const age =
    typeof cache.age_ms === 'number' && Number.isFinite(cache.age_ms)
      ? ` · age ${Math.round(cache.age_ms / 1000)}s`
      : '';
  return `Cache HIT${age}`;
}

/**
 * groupBy is the aggregation dimension (`tenant`), not the current tenant.
 * Prefer the session tenant label (e.g. prod) over the literal "tenant".
 */
export function formatTelemetryTenantScope(input: {
  groupBy?: string | null;
  sessionTenant?: string | null;
}): string {
  const session = String(input.sessionTenant || '').trim();
  if (session) return session;
  const groupBy = String(input.groupBy || '').trim();
  if (groupBy && groupBy.toLowerCase() !== 'tenant') return groupBy;
  return 'this tenant';
}
